import { IHttpService, IPromise, IQService } from 'angular';
import ActionDispatcherService from '../../actions/core/action-dispatcher.service';
import { RestResponse } from '../../models/rest.model';
import HttpService from '../http.service';
import PackageService from '../package/package.service';
import { UserResponse } from '../user/user.model';
import UtilService from '../util.service';
import { AuthGrantedAction, AuthRevokedAction, AuthType } from './auth.action';
import { LoginAttemptRejection, LoginError, LoginResponse } from './auth.model';

/* @ngInject */
export default class AuthService {
  private static extractToken(
    loginResponse: LoginResponse,
    rememberMe: boolean
  ): string {
    let token;
    if (rememberMe === true) {
      token = loginResponse.rememberMeToken;
    }
    return token || loginResponse.authToken;
  }

  private authConfig;
  private mcConfig;
  private readonly authorizationCookieName = '_sid';
  private readonly authenticationCookieName = 'hm_ltt';
  private readonly applicationAccessToken = 'otp_atkn';
  private readonly applicationRefreshToken = 'otp_rtkn';

  constructor(
    private $cookies,
    private $http: IHttpService,
    private $q: IQService,
    private config,
    private actionDispatcher: ActionDispatcherService,
    private httpService: HttpService,
    private packageService: PackageService
  ) {
    this.authConfig = this.config.auth;
    this.mcConfig = this.config.mobileConnect;
  }

  public getSid() {
    return this.$cookies.get(this.authorizationCookieName);
  }

  public authorize(protectAtkn?:boolean): IPromise<RestResponse<boolean>> {
    const { url, params } = this.authConfig.authorize;
    if(protectAtkn){
      params["protectAtkn"] = "true";
    }
    return this.httpService.extractResponseResult(
      this.$http.post(url, null, { params })
    );
  }

  public login(
    username: string,
    password: string,
    rememberMe: boolean = false,
    captchaToken?: string
  ): IPromise<UserResponse> {
    if (this.authConfig.loginWithAuthorize) {
      return this.doAuthenticateWithMsisdn(username, password).then(
        user => {
          this.actionDispatcher.dispatch(
            new AuthGrantedAction(user, AuthType.PASSWORDLOGIN)
          );
          return user;
        },
        reason => this.$q.reject(new LoginAttemptRejection(reason))
      );
    } else {
      return this.doLogin(username, password, rememberMe, captchaToken).then(
        loginResponse => {
          const token = AuthService.extractToken(loginResponse, rememberMe);
          if (token) {
            return this.doAuthenticate(token, rememberMe).then(
              user => {
                this.actionDispatcher.dispatch(
                  new AuthGrantedAction(user, AuthType.PASSWORDLOGIN)
                );
                return user;
              },
              reason => this.$q.reject(new LoginAttemptRejection(reason))
            );
          }
          return this.$q.reject(
            new LoginAttemptRejection(
              new LoginError(loginResponse.code, loginResponse.message),
              loginResponse.showCaptcha
            )
          );
        },
        reason => this.$q.reject(new LoginAttemptRejection(reason))
      );
    }
  }

  public mcLogin(
    msisdn: string,
    code: string,
    state: string
  ): IPromise<UserResponse> {
    return this.doMcAuthenticate(msisdn, code, state).then(
      user => {
        this.actionDispatcher.dispatch(
          new AuthGrantedAction(user, AuthType.MOBILECONNECT)
        );
        return user;
      },
      reason => this.$q.reject(new LoginAttemptRejection(reason))
    );
  }

  public doMcAuthenticate(
    msisdn: string,
    code: string,
    state: string
  ): IPromise<UserResponse> {
    const { url } = this.mcConfig.authenticate;
    const redirectUrl = this.mcConfig.redirect.url;
    const params = {
      code,
      msisdn,
      state,
      redirectUrl
    };
    return this.httpService.extractResponseResult(
      this.$http.post(url, params, null)
    );
  }

  public onAuthenticationDiscarded() {
    this.actionDispatcher.dispatch(new AuthRevokedAction());
  }

  public getUniqueCaptchaUrl() {
    return `${this.authConfig.captcha
      .url}?nocache=${UtilService.generateRandomNumber()}`;
  }

  public logout() {
    // we need only for global 2.0
    const url = this.authConfig.logout.url;

    return this.httpService.extractResponseResult(this.$http.get(url)).then(success => {
      
    }).finally(() => {
      this.packageService.userOfferablePackages = [];
      const cookies = this.$cookies.getAll();
      angular.forEach(cookies, (v, k) => this.$cookies.remove(k,{
        domain: this.config.cookieDomain
      }));
      this.$cookies.remove(this.authorizationCookieName,{
        domain: this.config.cookieDomain
      });
      this.$cookies.remove(this.applicationAccessToken,{
        domain: this.config.cookieDomain
      });
      this.$cookies.remove(this.applicationRefreshToken,{
        domain: this.config.cookieDomain
      });
    });
  }

  public getAfterLoginMessage(): angular.IPromise<any> {
    return this.httpService.extractResponseResult(
      this.$http.get('hafifmuzik/mobile/user/notifications')
    );
  }

  private doLogin(
    username: string,
    password: string,
    rememberMe: boolean,
    captchaToken: string
  ): IPromise<LoginResponse> {
    const { url } = this.authConfig.login;
    let { params } = this.authConfig.login;
    params = angular.extend({}, params, {
      captchaToken,
      password,
      rememberMe,
      username
    });
    return this.httpService.extractResponseData(
      this.$http.post(url, params, {
        headers: {
          Country: undefined
        }
      })
    );
  }

  private doAuthenticate(
    token: string,
    rememberMe: boolean
  ): IPromise<UserResponse> {
    const { url } = this.authConfig.authenticate;
    let { params } = this.authConfig.authenticate;
    params = angular.extend({}, params, {
      enableLongTermToken: rememberMe,
      token
    });
    return this.httpService.extractResponseResult(
      this.$http.post(url, null, { params })
    );
  }

  private doAuthenticateWithMsisdn(
    username: string,
    password: string
  ): IPromise<UserResponse> {
    const { url } = this.authConfig.authenticate;
    let { params } = this.authConfig.authenticate;
    params = angular.extend({}, params, {
      msisdn: username,
      password
    });
    return this.httpService.extractResponseResult(
      this.$http.post(url, null, { params })
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/common/services/auth/auth.service.ts